/**
 * ProcessSelector — ChatView header selector for the session's agent process
 * Rendered in place of the legacy mode selector when the dev toggle
 * is on OR the session already has a process set.
 *
 * Seeded defaults (is_system) list first, then account customs (ordering
 * applied by useAgentProcesses). Selection updates the session's
 * `agent_process_id` — takes effect on the next run.
 *
 * The session's CURRENT process renders from the session read model
 * (`agent_process_id` + joined `agent_process_name`) even while the process
 * library list is still loading: a controlled <select> whose value has no
 * matching option reports "" to the DOM, so without the synthesized option
 * the header shows a blank selector for as long as the list query takes
 * (observed for ~30s under full parallel e2e load). Once the list resolves
 * and contains the id, the real option takes over and the synthesized one
 * drops away.
 */

import { useAgentProcesses } from '../../hooks/useAgentProcesses';
import { isRespondOnly } from './process-graph';
import { SettingsMenuSelect } from '../chat/SettingsMenuRow';

interface ProcessSelectorProps {
  /** The session's current process (null on a not-yet-converted legacy session). */
  agentProcessId: string | null | undefined;
  /** Joined process name from the session read model. */
  agentProcessName?: string | null;
  /**
   * True when the session (or draft) has no workspace. Workspace-less
   * sessions can only run respond-only processes — executor/dynamic steps
   * need a workspace and the backend rejects them with 422 — so the
   * selector offers only the respond-only subset.
   */
  workspaceless?: boolean;
  onSelect: (processId: string) => void;
}

export function ProcessSelector({
  agentProcessId,
  agentProcessName,
  workspaceless = false,
  onSelect,
}: ProcessSelectorProps) {
  const { data: allProcesses } = useAgentProcesses();
  const processes = workspaceless
    ? allProcesses?.filter((p) => isRespondOnly(p.definition.graph))
    : allProcesses;
  const currentInList =
    agentProcessId != null && (processes?.some((p) => p.id === agentProcessId) ?? false);

  return (
    <SettingsMenuSelect
      data-testid="process-selector"
      aria-label="Process"
      value={agentProcessId ?? ''}
      onChange={(e) => {
        if (e.target.value) onSelect(e.target.value);
      }}
    >
      {!agentProcessId && <option value="">Select process...</option>}
      {agentProcessId && !currentInList && (
        <option value={agentProcessId}>{agentProcessName ?? 'Current process'}</option>
      )}
      {processes?.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </SettingsMenuSelect>
  );
}
